import { storage, path } from '@commons';

// Reten.cx (https://reten.cx)
//
// A tag <script src="https://reten.cx/r.js"> le os atributos data-account (accountCode da conta
// logada), data-user (email do usuario) e data-track (se a conta tem tracking habilitado) no
// momento em que carrega. Como esses dados so existem apos o login E a selecao da conta/fazenda,
// a tag NAO fica estatica no index.html: ela e injetada aqui, dinamicamente, quando ja temos
// accountCode + email.

const RETEN_SCRIPT_ID = 'reten-tracking';
const RETEN_SRC = 'https://reten.cx/r.js';
const RETEN_KEY = 'rtn_pk_live_0072c8f305644ea5947ead0cdb234092';

// Flag de "login pendente". O evento de login e disparado no login da plataforma
// (http.auth.service.js), mas so pode ser enviado ao Reten depois que a tag carrega
// (o que exige a conta selecionada). Como o login faz reload da pagina, guardamos o
// email aqui para consumir uma unica vez quando a tag estiver pronta.
const RETEN_PENDING_LOGIN = 'reten-pending-login';

// Nao trackeia usuarios internos (suporte / pesquisa), assim como os demais trackings
// (mixpanel ignora emails @agrare.com.br; customerX ignora perfis SUPORTE_AGRARE e ESTUDANTE_PESQUISA).
const isTrackingBlockedUser = (user) => {
    if (!user) return true;
    if (user.perfil === 'SUPORTE_AGRARE' || user.perfil === 'ESTUDANTE_PESQUISA') return true;
    if (user.email && user.email.includes('@agrare.com.br')) return true;
    return false;
};

const getSession = () => {
    const user = JSON.parse(storage.get(path.sessionUser));
    const configSessionRaw = storage.get('CONFIG_SESSION');
    const configSession = configSessionRaw ? JSON.parse(configSessionRaw) : null;
    return { user, contaAgrare: configSession?.contaAgrare ?? null };
};

// Carrega (ou recarrega) a tag do Reten com os atributos ja preenchidos.
const loadRetenScript = ({ account, user, track }) => {
    if (typeof document === 'undefined') return;

    // Se ja existe uma tag para a mesma conta/usuario/track, nao faz nada.
    const existing = document.getElementById(RETEN_SCRIPT_ID);
    if (
        existing &&
        existing.getAttribute('data-account') === String(account) &&
        existing.getAttribute('data-user') === String(user) &&
        existing.getAttribute('data-track') === String(track)
    ) {
        return;
    }

    // Conta/usuario/track mudou: remove a tag anterior antes de recarregar.
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = RETEN_SCRIPT_ID;
    script.src = RETEN_SRC;
    script.async = true;
    script.setAttribute('data-key', RETEN_KEY);
    script.setAttribute('data-account', String(account));
    script.setAttribute('data-user', String(user));
    // data-track="true" se a conta tem tracking habilitado, "false" caso contrario.
    script.setAttribute('data-track', String(track));
    document.head.appendChild(script);
};

// Dispara o reten.login (uma unica vez) se houver um login pendente marcado pelo login
// da plataforma. Chamado apos a tag do Reten carregar. Nao gera novos eventos em
// reloads ou trocas de fazenda, pois a flag ja foi consumida.
const flushPendingLogin = () => {
    const pendingEmail = storage.get(RETEN_PENDING_LOGIN);
    if (!pendingEmail) return;

    const send = () => {
        if (typeof window !== 'undefined' && window.reten && typeof window.reten.login === 'function') {
            window.reten.login(String(pendingEmail));
            storage.remove(RETEN_PENDING_LOGIN);
            return true;
        }
        return false;
    };

    // A tag e async: tenta agora e, se ainda nao carregou, tenta novamente ao carregar.
    if (!send()) {
        const script = document.getElementById(RETEN_SCRIPT_ID);
        if (script) script.addEventListener('load', send, { once: true });
    }
};

// Identifica o usuario/conta no Reten. So dispara quando ja temos accountCode + email
// e o usuario nao e interno (suporte). O tracking habilitado/desabilitado da conta e
// refletido no atributo data-track (nao impede o carregamento da tag).
export const identifyReten = () => {
    try {
        const { user, contaAgrare } = getSession();

        // Ignora usuarios internos (ex.: suporte), como os demais trackings.
        if (isTrackingBlockedUser(user)) return;

        const accountCode = contaAgrare?.accountCode ?? null;
        const email = user?.email ?? null;

        // Sem conta selecionada ou sem email nao ha o que identificar (ex.: logo apos o login,
        // antes de entrar em uma fazenda). A tag sera carregada quando a conta for selecionada.
        if (!accountCode || !email) return;

        loadRetenScript({
            account: accountCode,
            user: email,
            track: contaAgrare?.trackingEnabled === true,
        });

        // Se havia um login pendente da plataforma, envia agora que a tag esta carregando.
        flushPendingLogin();
    } catch (error) {
        console.error('[Reten] Erro ao identificar usuario:', error);
    }
};

// Registra a intencao de login na plataforma (login com sucesso em http.auth.service.js).
// O evento so e enviado ao Reten quando a tag carrega (identifyReten -> flushPendingLogin),
// e apenas uma vez. Como o login faz reload da pagina, a flag e persistida na sessao.
export const retenLogin = (userEmail) => {
    try {
        if (userEmail === undefined || userEmail === null) return;

        const { user } = getSession();

        // Ignora usuarios internos (ex.: suporte), como os demais trackings.
        if (isTrackingBlockedUser(user)) return;

        storage.set(RETEN_PENDING_LOGIN, String(userEmail));

        // Caso a tag ja esteja carregada (login sem reload), envia imediatamente.
        flushPendingLogin();
    } catch (error) {
        console.error('[Reten] Erro no login:', error);
    }
};

export default { identifyReten, retenLogin };
